import qz from "qz-tray";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

let isConnected = false;
let connectionPromise: Promise<void> | null = null;
let autoReconnectEnabled = false;
let cachedHostname: string | null = null;
let callbacksRegistered = false;
let onClosedCallback: (() => void) | null = null;
let onErrorCallback: ((error: Error) => void) | null = null;

// In-memory cached signing key for offline QZ Tray signing.
// Fetched once at boot (when authenticated + online) via the
// `qz-get-signing-key` edge function and used by `signRequest` to
// sign locally with Web Crypto, avoiding a round-trip to `qz-sign`
// and enabling printing while offline.
let cachedSigningKey: CryptoKey | null = null;
let signingKeyFetchPromise: Promise<CryptoKey | null> | null = null;

// ESC/POS Commands
const ESC = "\x1B";
const GS = "\x1D";

export type PrinterProtocol = "epson_standard" | "bematech_legacy" | "generic";

// Protocol-specific commands
export const PROTOCOL_COMMANDS: Record<PrinterProtocol, {
  INIT: string;
  CUT_FULL: string;
  CUT_PARTIAL: string;
  DRAWER_KICK: string;
}> = {
  epson_standard: {
    INIT: ESC + "@",
    CUT_FULL: GS + "V" + "\x00",
    CUT_PARTIAL: GS + "V" + "\x01",
    DRAWER_KICK: ESC + "p" + "\x00" + "\x19" + "\xFA",
  },
  bematech_legacy: {
    INIT: ESC + "@",
    CUT_FULL: ESC + "m",        // Different command for old Bematech
    CUT_PARTIAL: ESC + "m",
    DRAWER_KICK: ESC + "p" + "\x00" + "\x32" + "\xFF",
  },
  generic: {
    INIT: ESC + "@",
    CUT_FULL: GS + "V" + "\x00", // Use standard as fallback
    CUT_PARTIAL: GS + "V" + "\x01",
    DRAWER_KICK: ESC + "p" + "\x00" + "\x19" + "\xFA",
  },
};

export const ESCPOS = {
  INIT: ESC + "@",                    // Initialize printer
  ALIGN_CENTER: ESC + "a" + "\x01",   // Center align
  ALIGN_LEFT: ESC + "a" + "\x00",     // Left align
  BOLD_ON: ESC + "E" + "\x01",        // Bold on
  BOLD_OFF: ESC + "E" + "\x00",       // Bold off
  DOUBLE_SIZE_ON: GS + "!" + "\x11",  // Double width + double height
  DOUBLE_SIZE_OFF: GS + "!" + "\x00", // Normal size
  CUT_FULL: GS + "V" + "\x00",        // Full cut (default, use PROTOCOL_COMMANDS for protocol-specific)
  CUT_PARTIAL: GS + "V" + "\x01",     // Partial cut
  FEED_LINE: "\n",                     // Line feed
};

export interface PrinterSettings {
  paper_width: "58mm" | "80mm";
  chars_per_line: number;
  auto_cut_enabled: boolean;
  cut_type: "full" | "partial";
  extra_lines_before_cut: number;
  protocol?: PrinterProtocol;
}

export interface PrintJob {
  printerName: string;
  content: string;
  logType?: string;
  logIdentifier?: string | number;
  locationName?: string;
  settings?: PrinterSettings;
}

/**
 * Normalize text for thermal printer compatibility.
 * Removes accents and special characters that ESC/POS printers can't handle.
 */
export function normalizeForPrinter(text: string): string {
  return text
    // Normalize Unicode currency format (Intl generates R$ with non-breaking space)
    .replace(/R\$\u00A0/g, "R$ ")
    .replace(/R\$\s/g, "R$ ")
    // Remove accents using Unicode decomposition
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    // Handle cedilla separately (ç -> c)
    .replace(/ç/g, "c")
    .replace(/Ç/g, "C")
    // Replace any remaining non-ASCII characters
    .replace(/[^\x00-\x7F]/g, "");
}

// Certificate for sejaaltus.com.br (self-signed, valid 2026-2036)
const QZ_CERTIFICATE = `-----BEGIN CERTIFICATE-----
MIIDFzCCAf+gAwIBAgIUUNe3sSzzfmbfwj8PBdNlIsYMNEkwDQYJKoZIhvcNAQEL
BQAwGzEZMBcGA1UEAwwQc2VqYWFsdHVzLmNvbS5icjAeFw0yNjAzMDQwMDMwNTVa
Fw0zNjAzMDEwMDMwNTVaMBsxGTAXBgNVBAMMEHNlamFhbHR1cy5jb20uYnIwggEi
MA0GCSqGSIb3DQEBAQUAA4IBDwAwggEKAoIBAQDRZxpDd4FP+NXrLxHjjKt+i9BP
wULn0VLrj4QWT1omZrtnxYacts+zmY0I18M5cj5616TxgIsTTvw2QQOpxRAmO1Fu
OvJMTOUW85Wf3cPLVegnvQ98DjBN5sMH7TuZ1KRJWqcdFh2OvjztVmtjMaAjYQqd
aucotqQS2o7Rx53nr4NvllvGgNX2s6VQVOrCtytcXsMTLjEVnjOSwWmJaZyMzUrj
ZzuGznB5n1fRByaW0HdHywGHmZOjBqOodeHVi6xORVKcKTNBKNFwIsRyfE54oHdB
olRTps6QXrrPkWs+JXo9kaDV6wbl5jNihiAJpOBEZV151SIpNZ3xOg2XrBWpAgMB
AAGjUzBRMB0GA1UdDgQWBBQs4LEYv+i9Zor5tffg/zXGNbmeLTAfBgNVHSMEGDAW
gBQs4LEYv+i9Zor5tffg/zXGNbmeLTAPBgNVHRMBAf8EBTADAQH/MA0GCSqGSIb3
DQEBCwUAA4IBAQBNR8AKxgknTsqJO/PSGV0kY4naIsPRroZIKk5oHSwchwMd8VcD
8UVX3/7/sVvJoi7JCDvUDpm7JAX8/ETAOHmQPuONgYMES4N5FPWJo8Jvg4oCXeMV
dluVEHXZSGBIfjU4Izx4kg3UyM39LkT/ZE6GfFlnSEzOy/iAQvfqgOim5Ami8E/Z
WOMkH90bD6CPeEU1fpx6hgUlrKVSsfODyB0222CMcsI7hoU/HpEAXRcN7MA0c739
HA/UZnPfQJisIOv91vdzgfkvnqdadUYSU0pWrxRlhgM66G2ocv21oyVFoBjWw95n
BizUK00Wl5psclpv2gaXmEcM53Ay/sIg34cZ
-----END CERTIFICATE-----`;

// Function to sign requests via Edge Function
async function signRequest(toSign: string): Promise<string> {
  console.log('[QZ] signRequest called');
  
  if (!toSign || typeof toSign !== 'string') {
    console.error('[QZ] signRequest: toSign is empty or not a string');
    throw new Error('toSign parameter is required and must be a string');
  }

  // 1) Try local signing with the in-memory cached key (works online and offline).
  try {
    const local = await signLocally(toSign);
    if (local) return local;
  } catch (e) {
    console.warn('[QZ] signLocally failed, falling back to Edge Function:', e);
  }

  // 2) Fallback: Edge Function (original behavior).
  try {
    const { data, error } = await supabase.functions.invoke('qz-sign', {
      body: { toSign }
    });

    if (error) {
      console.error('[QZ] Error signing request:', error);
      throw error;
    }

    console.log('[QZ] Signature received successfully');
    return data.signature;
  } catch (err) {
    console.error('[QZ] Failed to sign request:', err);
    throw err;
  }
}

/**
 * Fetches the QZ signing private key from the edge function and imports it
 * as a non-extractable CryptoKey held only in module memory. Idempotent and
 * safe to call multiple times; never throws.
 */
async function loadSigningKey(): Promise<CryptoKey | null> {
  if (cachedSigningKey) return cachedSigningKey;
  if (signingKeyFetchPromise) return signingKeyFetchPromise;

  signingKeyFetchPromise = (async () => {
    try {
      // Guard: don't call the edge function before the user is authenticated.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      const { data, error } = await supabase.functions.invoke('qz-get-signing-key');
      if (error || !data?.privateKeyPkcs8Base64) {
        console.warn('[QZ] loadSigningKey: edge function unavailable', error);
        return null;
      }

      const der = Uint8Array.from(atob(data.privateKeyPkcs8Base64), (c) => c.charCodeAt(0));
      const key = await crypto.subtle.importKey(
        'pkcs8',
        der,
        { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-512' },
        false,
        ['sign']
      );
      cachedSigningKey = key;
      console.log('[QZ] Signing key cached — offline printing enabled');
      return key;
    } catch (e) {
      console.warn('[QZ] loadSigningKey failed:', e);
      return null;
    } finally {
      signingKeyFetchPromise = null;
    }
  })();

  return signingKeyFetchPromise;
}

/**
 * Signs `toSign` locally using the cached key. Returns null when the key
 * is not loaded so the caller can fall back to the edge function.
 * Output format matches `qz-sign`: btoa over the raw signature bytes.
 */
async function signLocally(toSign: string): Promise<string | null> {
  if (!cachedSigningKey) return null;
  const sig = await crypto.subtle.sign(
    'RSASSA-PKCS1-v1_5',
    cachedSigningKey,
    new TextEncoder().encode(toSign)
  );
  return btoa(String.fromCharCode(...new Uint8Array(sig)));
}

// Register global callbacks (once)
function setupGlobalCallbacks() {
  if (callbacksRegistered) return;
  
  try {
    qz.websocket.setClosedCallbacks(() => {
      console.log("[QZ] WebSocket closed");
      isConnected = false;
      
      if (onClosedCallback) {
        onClosedCallback();
      }
      
      // Show toast only if auto-reconnect is not handling it
      if (!autoReconnectEnabled) {
        toast.warning("Conexão com impressora perdida", {
          description: "Reconecte na página de Impressoras"
        });
      }
    });

    qz.websocket.setErrorCallbacks((error: Error) => {
      console.error("[QZ] WebSocket error:", error);
      isConnected = false;
      
      if (onErrorCallback) {
        onErrorCallback(error);
      }
    });
    
    callbacksRegistered = true;
    console.log("[QZ] Global callbacks registered");
  } catch (e) {
    console.warn("[QZ] Could not register callbacks:", e);
  }
}

export function registerConnectionCallbacks(
  onClosed: () => void,
  onError: (error: Error) => void
) {
  onClosedCallback = onClosed;
  onErrorCallback = onError;
}

export function setAutoReconnectEnabled(enabled: boolean) {
  autoReconnectEnabled = enabled;
}

export function getAutoReconnectEnabled(): boolean {
  return autoReconnectEnabled;
}

export function getTerminalName(): string | null {
  return cachedHostname;
}

export async function connectQZTray(): Promise<boolean> {
  // Check real connection state first
  if (qz.websocket.isActive()) {
    isConnected = true;
    return true;
  }
  
  // Reset state if we thought we were connected but aren't
  if (isConnected && !qz.websocket.isActive()) {
    console.log("[QZ] State mismatch - resetting connection state");
    isConnected = false;
  }
  
  if (connectionPromise) {
    await connectionPromise;
    return isConnected;
  }

  connectionPromise = new Promise(async (resolve) => {
    try {
      // Setup callbacks before connecting
      setupGlobalCallbacks();

      // Pre-fetch signing key for offline use. Fire-and-forget; has internal
      // guards (session + idempotency) and never throws.
      void loadSigningKey();

      // Configure certificate
      qz.security.setCertificatePromise(function(resolve: (cert: string) => void) {
        resolve(QZ_CERTIFICATE);
      });

      // Set signature algorithm to SHA512
      qz.security.setSignatureAlgorithm("SHA512");

      // Configure signature using Edge Function
      qz.security.setSignaturePromise((toSign: string) => {
        return (resolve: (sig: string) => void, reject: (err: Error) => void) => {
          signRequest(toSign)
            .then(signature => resolve(signature))
            .catch(err => reject(err));
        };
      });

      // Connect with retry and keepAlive options
      await qz.websocket.connect({
        retries: 3,
        delay: 1,
        keepAlive: 30 // Ping every 30s to prevent idle timeout
      });
      
      isConnected = true;
      console.log("[QZ] Connected successfully");
      
      // Capture hostname for terminal identification in prints
      try {
        cachedHostname = await qz.networking.hostname();
        console.log("[QZ] Hostname:", cachedHostname);
      } catch (e) {
        console.warn("[QZ] Could not get hostname:", e);
      }
      
      // Ensure log folders exist in sandbox
      try {
        const { ensurePrintLogFolders } = await import("@/lib/print-log");
        await ensurePrintLogFolders();
      } catch (e) {
        console.warn("[QZ] Could not ensure log folders:", e);
      }
      
      // Process pending print queue
      try {
        const { processPendingPrintJobs, getPendingPrintCount } = await import("@/lib/print-queue");
        const pendingCount = getPendingPrintCount();
        if (pendingCount > 0) {
          console.log(`[QZ] Processing ${pendingCount} pending print jobs...`);
          // Process in background after a short delay
          setTimeout(() => {
            processPendingPrintJobs();
          }, 1000);
        }
      } catch (e) {
        console.warn("[QZ] Could not process pending queue:", e);
      }
      
      if (autoReconnectEnabled) {
        toast.success("Impressora conectada");
      }
      
      resolve();
    } catch (error: any) {
      console.error("[QZ] Connection error:", error);
      isConnected = false;
      
      if (error.message?.includes("Unable to establish connection")) {
        if (!autoReconnectEnabled) {
          toast.error("QZ Tray não encontrado", {
            description: "Verifique se está instalado e rodando"
          });
        }
      } else if (error.message?.includes("signature")) {
        toast.error("Erro na assinatura do QZ Tray");
      }
      resolve();
    }
  });

  await connectionPromise;
  connectionPromise = null;
  return isConnected;
}

export async function disconnectQZTray(): Promise<void> {
  if (qz.websocket.isActive()) {
    await qz.websocket.disconnect();
    isConnected = false;
    console.log("[QZ] Disconnected");
  }
}

// Check if QZ WebSocket is actually active
export function isQZWebSocketActive(): boolean {
  try {
    return qz.websocket.isActive();
  } catch {
    return false;
  }
}

export function isQZConnected(): boolean {
  // Return true only if both our state and the actual socket are connected
  return isConnected && isQZWebSocketActive();
}

// Ensure connection before printing - with retry
export async function ensureQZConnected(): Promise<boolean> {
  if (isQZWebSocketActive()) {
    return true;
  }
  
  console.log("[QZ] Ensuring connection...");
  
  // Try to connect up to 2 times
  for (let i = 0; i < 2; i++) {
    const success = await connectQZTray();
    if (success) {
      return true;
    }
    
    if (i < 1) {
      // Wait a bit before retrying
      await new Promise(r => setTimeout(r, 1000));
    }
  }
  
  return false;
}

export async function getPrinters(): Promise<string[]> {
  const connected = await ensureQZConnected();
  if (!connected) return [];

  try {
    const printers = await qz.printers.find();
    return Array.isArray(printers) ? printers : [printers];
  } catch (error) {
    console.error("[QZ] Error getting printers:", error);
    return [];
  }
}

// Helper to generate separator lines based on paper width
export function getSeparator(chars: number = 48, char: string = "-"): string {
  return char.repeat(chars) + "\n";
}

// Helper to add cut command based on settings and protocol
export function getCutCommand(settings?: PrinterSettings): string {
  if (!settings?.auto_cut_enabled) return "\n\n\n";
  
  const protocol = settings.protocol || "epson_standard";
  const commands = PROTOCOL_COMMANDS[protocol];
  
  let result = "";
  // Add extra lines before cut
  const extraLines = settings.extra_lines_before_cut ?? 3;
  for (let i = 0; i < extraLines; i++) {
    result += "\n";
  }
  // Add cut command based on protocol
  result += settings.cut_type === "partial" ? commands.CUT_PARTIAL : commands.CUT_FULL;
  return result;
}

// Fetch printer settings from database
export async function getPrinterSettings(printerName: string, restaurantId: string): Promise<PrinterSettings | null> {
  try {
    const { data, error } = await supabase
      .from("printers")
      .select("paper_width, chars_per_line, auto_cut_enabled, cut_type, extra_lines_before_cut, protocol")
      .eq("restaurant_id", restaurantId)
      .eq("printer_name", printerName)
      .eq("is_active", true)
      .single();
    
    if (error) {
      // Silently handle 406 errors - likely PWA cache corruption
      if (error.code === '406' || error.message?.includes('406')) {
        console.warn('[getPrinterSettings] Cache corruption detected (406), using defaults');
      }
      return null;
    }
    
    if (!data) return null;
    
    return {
      paper_width: (data.paper_width || "80mm") as "58mm" | "80mm",
      chars_per_line: data.chars_per_line || 48,
      auto_cut_enabled: data.auto_cut_enabled ?? true,
      cut_type: (data.cut_type || "full") as "full" | "partial",
      extra_lines_before_cut: data.extra_lines_before_cut ?? 3,
      protocol: (data.protocol || "epson_standard") as PrinterProtocol,
    };
  } catch {
    return null;
  }
}

export async function printReceipt(
  printerName: string, 
  content: string,
  options?: { 
    skipLog?: boolean; 
    logType?: string; 
    logIdentifier?: string | number; 
    locationName?: string;
    settings?: PrinterSettings;
  }
): Promise<boolean> {
  const connected = await ensureQZConnected();
  if (!connected) {
    toast.error("Não foi possível conectar ao QZ Tray", {
      description: "Verifique se o QZ Tray está rodando"
    });
    return false;
  }

  try {
    // Configure with paper width if settings provided
    const configOptions: Record<string, unknown> = {};
    if (options?.settings?.paper_width) {
      configOptions.size = { width: options.settings.paper_width === "80mm" ? 80 : 58 };
      configOptions.units = "mm";
    }
    
    const config = qz.configs.create(printerName, configOptions);
    
    // Add initialization and cut commands to content
    let finalContent = ESCPOS.INIT + content;
    if (options?.settings) {
      finalContent += getCutCommand(options.settings);
    } else {
      // Default: add basic cut
      finalContent += "\n\n\n" + ESCPOS.CUT_FULL;
    }
    
    const data = [
      { type: "raw", format: "plain", data: finalContent }
    ];

    await qz.print(config, data);
    console.log(`[QZ] Printed to ${printerName}`);
    
    // Save log file if not skipped
    if (!options?.skipLog) {
      try {
        const logType = options?.logType || "pedidos";
        const now = new Date();
        const date = now.toISOString().split("T")[0];
        const time = now.toTimeString().split(" ")[0].replace(/:/g, "-");
        const identifier = options?.logIdentifier || "";
        const locationPart = options?.locationName ? `_${options.locationName.replace(/[^a-zA-Z0-9]/g, "")}` : "";
        const filename = `${logType}_${identifier ? identifier + "_" : ""}${locationPart}${date}_${time}.txt`;
        
        await qz.file.write(filename, {
          data: content,
          sandbox: true,
          shared: false,
          append: false,
        });
        console.log(`[QZ] Log saved: ${filename}`);
      } catch (logError) {
        console.warn("[QZ] Failed to save log:", logError);
        toast.warning("Backup local não salvo", {
          description: "Impressão enviada, mas log local falhou"
        });
      }
    }
    
    return true;
  } catch (error) {
    console.error(`[QZ] Error printing to ${printerName}:`, error);
    toast.error(`Erro ao imprimir em ${printerName}`);
    return false;
  }
}

export async function printMultiple(jobs: PrintJob[]): Promise<void> {
  const connected = await ensureQZConnected();
  if (!connected) {
    toast.error("Não foi possível conectar ao QZ Tray");
    return;
  }

  for (const job of jobs) {
    await printReceipt(job.printerName, job.content, {
      logType: job.logType,
      logIdentifier: job.logIdentifier,
      locationName: job.locationName,
    });
  }
}

export interface DeliveryInfo {
  isDelivery: boolean;
  deliveryAddress?: string;
  customerPhone?: string;
  isPaid?: boolean;
  total?: number;
  deliveryFee?: number;
}

export function formatOrderReceipt(
  orderNumber: number,
  items: Array<{ 
    name: string; 
    quantity: number; 
    notes?: string; 
    selectedAddons?: Array<{ name: string; price: number }>;
    comboId?: string;
    comboSelections?: {
      selectedFlavors?: Array<{ productName: string }>;
      selectedChoices?: Array<{ choiceLabel: string; productName: string }>;
      slotSelections?: Array<{ slotLabel?: string; selectedProducts?: Array<{ productName: string }> }> | Record<string, Array<{ productName: string }>>;
    };
  }>,
  locationName: string,
  orderType: string,
  tableOrTab?: string,
  customerName?: string,
  waiterName?: string,
  orderNotes?: string,
  charsPerLine: number = 48,
  deliveryInfo?: DeliveryInfo,
  sourceName?: string
): string {
  const date = new Date();
  const dateStr = date.toLocaleDateString("pt-BR");
  const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  const sep = "=".repeat(charsPerLine) + "\n";
  const sepLight = "-".repeat(charsPerLine) + "\n";

  let receipt = "";
  receipt += sep;
  
  // DELIVERY BANNER - prominent at top
  if (deliveryInfo?.isDelivery) {
    receipt += ESCPOS.ALIGN_CENTER;
    receipt += ESCPOS.DOUBLE_SIZE_ON + ESCPOS.BOLD_ON;
    receipt += "*** DELIVERY ***\n";
    receipt += ESCPOS.DOUBLE_SIZE_OFF + ESCPOS.BOLD_OFF;
    receipt += ESCPOS.ALIGN_LEFT;
  }
  
  const centerPad = Math.max(0, Math.floor((charsPerLine - locationName.length) / 2));
  receipt += " ".repeat(centerPad) + locationName.toUpperCase() + "\n";
  receipt += sep;
  receipt += `Pedido: #${orderNumber}\n`;
  receipt += `Data: ${dateStr} ${timeStr}\n`;
  if (sourceName) {
    receipt += `Terminal: ${normalizeForPrinter(sourceName)}\n`;
  }
  
  // Show delivery address for delivery orders
  if (deliveryInfo?.isDelivery && deliveryInfo.deliveryAddress) {
    receipt += sepLight;
    receipt += ESCPOS.BOLD_ON;
    receipt += "ENTREGAR EM:\n";
    receipt += ESCPOS.BOLD_OFF;
    receipt += `${normalizeForPrinter(deliveryInfo.deliveryAddress)}\n`;
    if (customerName) {
      receipt += `Cliente: ${normalizeForPrinter(customerName)}\n`;
    }
    if (deliveryInfo.customerPhone) {
      receipt += `Tel: ${normalizeForPrinter(deliveryInfo.customerPhone)}\n`;
    }
  } else if (orderType === "table" && tableOrTab) {
    receipt += `Mesa: ${tableOrTab}\n`;
    if (customerName) {
      receipt += `Cliente: ${normalizeForPrinter(customerName)}\n`;
    }
  } else if (orderType === "tab" && tableOrTab) {
    receipt += `Comanda: ${tableOrTab}\n`;
    if (customerName) {
      receipt += `Cliente: ${normalizeForPrinter(customerName)}\n`;
    }
  } else {
    receipt += `Tipo: Balcao\n`;
    if (customerName) {
      receipt += `Cliente: ${normalizeForPrinter(customerName)}\n`;
    }
  }
  
  if (waiterName) {
    receipt += `Garcom: ${normalizeForPrinter(waiterName)}\n`;
  }
  
  receipt += sepLight;
  receipt += "ITENS:\n";
  receipt += sepLight;

  // Items in double size + bold for better visibility
  receipt += ESCPOS.DOUBLE_SIZE_ON + ESCPOS.BOLD_ON;
  for (const item of items) {
    receipt += `${item.quantity} ${normalizeForPrinter(item.name)}\n`;
  }
  receipt += ESCPOS.DOUBLE_SIZE_OFF + ESCPOS.BOLD_OFF;

  // Item details: combo selections, addons and notes in normal size
  for (const item of items) {
    // Print combo selections (pizza flavors or choices)
    if (item.comboSelections) {
      // Pizza flavors (legacy format)
      if (item.comboSelections.selectedFlavors && item.comboSelections.selectedFlavors.length > 0) {
        const flavorCount = item.comboSelections.selectedFlavors.length;
        const flavorNames = item.comboSelections.selectedFlavors.map(f => f.productName);
        receipt += `  Sabores (${flavorCount}): ${normalizeForPrinter(flavorNames.join(", "))}\n`;
      }
      // Normal combo choices (legacy format)
      if (item.comboSelections.selectedChoices && item.comboSelections.selectedChoices.length > 0) {
        for (const choice of item.comboSelections.selectedChoices) {
          receipt += `  ${normalizeForPrinter(choice.choiceLabel)}: ${normalizeForPrinter(choice.productName)}\n`;
        }
      }
      // Modern slot selections format (PDV and Tablet)
      if (item.comboSelections.slotSelections) {
        if (Array.isArray(item.comboSelections.slotSelections)) {
          // Array format (PDV legacy)
          for (const slot of item.comboSelections.slotSelections) {
            if (slot.selectedProducts && slot.selectedProducts.length > 0) {
              for (const product of slot.selectedProducts) {
                receipt += `  > ${normalizeForPrinter(product.productName)}\n`;
              }
            }
          }
        } else {
          // Object format (tablet) - keys are slot IDs
          for (const products of Object.values(item.comboSelections.slotSelections)) {
            if (Array.isArray(products)) {
              for (const product of products as any[]) {
                receipt += `  > ${normalizeForPrinter(product.productName)}\n`;
              }
            }
          }
        }
      }
    }
    // Print addons if any
    if (item.selectedAddons && item.selectedAddons.length > 0) {
      for (const addon of item.selectedAddons) {
        receipt += `  + ${normalizeForPrinter(addon.name)}\n`;
      }
    }
    if (item.notes) {
      receipt += `  OBS: ${normalizeForPrinter(item.notes)}\n`;
    }
  }

  if (orderNotes) {
    receipt += sepLight;
    receipt += `OBS GERAL: ${normalizeForPrinter(orderNotes)}\n`;
  }

  receipt += sepLight;
  receipt += `Impresso: ${timeStr}\n`;
  receipt += sep;
  
  // Software footer
  receipt += ESCPOS.ALIGN_CENTER;
  receipt += "Altus Food\n";
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += "\n\n";

  return receipt;
}

// Delivery Driver Receipt (Via do Entregador)
export interface DeliveryReceiptData {
  orderNumber: number;
  customerName?: string;
  customerPhone?: string;
  deliveryAddress: string;
  items: Array<{
    name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
  }>;
  subtotal: number;
  deliveryFee?: number;
  total: number;
  isPaid: boolean;
  orderNotes?: string;
}

export function formatDeliveryReceipt(data: DeliveryReceiptData, charsPerLine: number = 48): string {
  const date = new Date();
  const dateStr = date.toLocaleDateString("pt-BR");
  const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  const formatCurrency = (value: number) =>
    normalizeForPrinter(new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value));

  const sep = "=".repeat(charsPerLine) + "\n";
  const sepLight = "-".repeat(charsPerLine) + "\n";

  let receipt = "";
  receipt += sep;
  receipt += ESCPOS.ALIGN_CENTER;
  receipt += ESCPOS.DOUBLE_SIZE_ON + ESCPOS.BOLD_ON;
  receipt += "PEDIDO DELIVERY\n";
  receipt += ESCPOS.DOUBLE_SIZE_OFF + ESCPOS.BOLD_OFF;
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += sep;
  
  receipt += `Pedido: #${data.orderNumber}\n`;
  receipt += `Data: ${dateStr} ${timeStr}\n`;
  
  receipt += sepLight;
  receipt += ESCPOS.BOLD_ON;
  receipt += "CLIENTE:\n";
  receipt += ESCPOS.BOLD_OFF;
  if (data.customerName) {
    receipt += `${normalizeForPrinter(data.customerName)}\n`;
  }
  if (data.customerPhone) {
    receipt += `Tel: ${normalizeForPrinter(data.customerPhone)}\n`;
  }
  
  receipt += sepLight;
  receipt += ESCPOS.BOLD_ON;
  receipt += "ENDERECO DE ENTREGA:\n";
  receipt += ESCPOS.BOLD_OFF;
  receipt += `${normalizeForPrinter(data.deliveryAddress)}\n`;
  
  receipt += sepLight;
  receipt += "ITENS:\n";
  receipt += sepLight;
  
  for (const item of data.items) {
    const itemText = `${item.quantity}x ${normalizeForPrinter(item.name)}`;
    const priceText = formatCurrency(item.total_price);
    const padding = Math.max(1, charsPerLine - itemText.length - priceText.length);
    receipt += itemText + " ".repeat(padding) + priceText + "\n";
  }
  
  receipt += sepLight;
  receipt += `Subtotal:${" ".repeat(Math.max(1, charsPerLine - 9 - formatCurrency(data.subtotal).length))}${formatCurrency(data.subtotal)}\n`;
  
  if (data.deliveryFee && data.deliveryFee > 0) {
    receipt += `Taxa Entrega:${" ".repeat(Math.max(1, charsPerLine - 13 - formatCurrency(data.deliveryFee).length))}${formatCurrency(data.deliveryFee)}\n`;
  }
  
  receipt += sepLight;
  receipt += ESCPOS.BOLD_ON;
  receipt += `TOTAL:${" ".repeat(Math.max(1, charsPerLine - 6 - formatCurrency(data.total).length))}${formatCurrency(data.total)}\n`;
  receipt += ESCPOS.BOLD_OFF;
  
  // Order notes/observations
  if (data.orderNotes) {
    receipt += sepLight;
    receipt += ESCPOS.BOLD_ON;
    receipt += "OBSERVACOES:\n";
    receipt += ESCPOS.BOLD_OFF;
    receipt += `${normalizeForPrinter(data.orderNotes)}\n`;
  }
  
  receipt += sep;
  
  // PAYMENT STATUS - Very prominent
  receipt += ESCPOS.ALIGN_CENTER;
  receipt += ESCPOS.DOUBLE_SIZE_ON + ESCPOS.BOLD_ON;
  if (data.isPaid) {
    receipt += "*** PAGO ***\n";
    receipt += "NAO RECEBER\n";
    receipt += "DO CLIENTE\n";
  } else {
    receipt += "*** COBRAR ***\n";
    receipt += formatCurrency(data.total) + "\n";
    receipt += "DO CLIENTE\n";
  }
  receipt += ESCPOS.DOUBLE_SIZE_OFF + ESCPOS.BOLD_OFF;
  receipt += ESCPOS.ALIGN_LEFT;
  
  receipt += sep;
  
  // Software footer
  receipt += ESCPOS.ALIGN_CENTER;
  receipt += "Altus Food\n";
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += "\n\n";

  return receipt;
}

interface CustomerReceiptData {
  orderNumber: number;
  items: {
    name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
  }[];
  subtotal: number;
  serviceTax?: number;
  discount?: number;
  total: number;
  paymentMethod: string;
  amountReceived?: number;
  change?: number;
  customerName?: string;
  restaurantName?: string;
  restaurantAddress?: string;
  // Entry (entrada de comanda)
  entryTypeName?: string;
  entryAmount?: number;
  entryMode?: "fixed" | "minimum_consumption";
  entryDifference?: number;
  // Couvert
  couvertAmount?: number;
  couvertQuantity?: number;
}

export function formatCustomerReceipt(data: CustomerReceiptData, charsPerLine: number = 48, template?: {
  title_bold: boolean;
  title_size: "normal" | "double";
  header_text: string | null;
  items_bold: boolean;
  items_size: "normal" | "double";
  totals_bold: boolean;
  totals_size: "normal" | "double";
  footer_text: string | null;
  show_software_footer: boolean;
  separator_char: string;
}): string {
  const date = new Date();
  const dateStr = date.toLocaleDateString("pt-BR");
  const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  const formatCurrency = (value: number) =>
    normalizeForPrinter(new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value));

  // Use template settings or defaults
  const separatorChar = template?.separator_char || "=";
  const sep = separatorChar.repeat(charsPerLine) + "\n";
  const sepLight = "-".repeat(charsPerLine) + "\n";
  const title = "RECIBO - VIA DO CLIENTE";
  const centerPad = Math.max(0, Math.floor((charsPerLine - title.length) / 2));

  // Group identical items (same name and unit price)
  const groupedItems = data.items.reduce((acc, item) => {
    const key = `${item.name}-${item.unit_price}`;
    if (acc.has(key)) {
      const existing = acc.get(key)!;
      existing.quantity += item.quantity;
      existing.total_price += item.total_price;
    } else {
      acc.set(key, { ...item });
    }
    return acc;
  }, new Map<string, typeof data.items[0]>());
  const consolidatedItems = Array.from(groupedItems.values());

  // Helper functions for formatting based on template
  const getTitleStart = () => {
    let cmd = "";
    if (template?.title_bold) cmd += ESCPOS.BOLD_ON;
    if (template?.title_size === "double") cmd += ESCPOS.DOUBLE_SIZE_ON;
    return cmd;
  };
  const getTitleEnd = () => {
    let cmd = "";
    if (template?.title_size === "double") cmd += ESCPOS.DOUBLE_SIZE_OFF;
    if (template?.title_bold) cmd += ESCPOS.BOLD_OFF;
    return cmd;
  };
  const getItemsStart = () => {
    let cmd = "";
    if (template?.items_bold !== false) cmd += ESCPOS.BOLD_ON;
    if (template?.items_size !== "normal") cmd += ESCPOS.DOUBLE_SIZE_ON;
    return cmd;
  };
  const getItemsEnd = () => {
    let cmd = "";
    if (template?.items_size !== "normal") cmd += ESCPOS.DOUBLE_SIZE_OFF;
    if (template?.items_bold !== false) cmd += ESCPOS.BOLD_OFF;
    return cmd;
  };
  const getTotalsStart = () => {
    let cmd = "";
    if (template?.totals_bold !== false) cmd += ESCPOS.BOLD_ON;
    if (template?.totals_size === "double") cmd += ESCPOS.DOUBLE_SIZE_ON;
    return cmd;
  };
  const getTotalsEnd = () => {
    let cmd = "";
    if (template?.totals_size === "double") cmd += ESCPOS.DOUBLE_SIZE_OFF;
    if (template?.totals_bold !== false) cmd += ESCPOS.BOLD_OFF;
    return cmd;
  };

  let receipt = "";
  receipt += sep;
  receipt += getTitleStart();
  receipt += ESCPOS.ALIGN_CENTER;
  receipt += title + "\n";
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += getTitleEnd();
  receipt += sep;
  
  // Header text from template
  if (template?.header_text) {
    receipt += ESCPOS.ALIGN_CENTER;
    receipt += normalizeForPrinter(template.header_text) + "\n";
    receipt += ESCPOS.ALIGN_LEFT;
  }
  
  if (data.restaurantName) {
    receipt += `${normalizeForPrinter(data.restaurantName)}\n`;
  }
  if (data.restaurantAddress) {
    receipt += `${normalizeForPrinter(data.restaurantAddress)}\n`;
  }
  
  receipt += `Data: ${dateStr} ${timeStr}\n`;
  
  if (data.customerName) {
    receipt += `Cliente: ${normalizeForPrinter(data.customerName)}\n`;
  }
  
  receipt += sepLight;
  receipt += "ITENS:\n";
  receipt += sepLight;

  // Items agrupados com valor na mesma linha
  receipt += getItemsStart();
  for (const item of consolidatedItems) {
    // Formato: "2x Produto            R$ 24,00"
    const itemText = `${item.quantity}x ${normalizeForPrinter(item.name)}`;
    const priceText = formatCurrency(item.total_price);
    const padding = Math.max(1, charsPerLine - itemText.length - priceText.length);
    receipt += itemText + " ".repeat(padding) + priceText + "\n";
  }
  receipt += getItemsEnd();

  receipt += sepLight;
  receipt += `Subtotal: ${formatCurrency(data.subtotal)}\n`;
  
  if (data.discount && data.discount > 0) {
    receipt += `Desconto: -${formatCurrency(data.discount)}\n`;
  }
  
  if (data.serviceTax && data.serviceTax > 0) {
    receipt += `Taxa Servico: +${formatCurrency(data.serviceTax)}\n`;
  }

  // Couvert
  if (data.couvertAmount && data.couvertAmount > 0) {
    const qty = data.couvertQuantity && data.couvertQuantity > 0 ? `${data.couvertQuantity}x ` : "";
    receipt += `Couvert ${qty}: +${formatCurrency(data.couvertAmount)}\n`;
  }

  // Entrada (entry)
  if (data.entryTypeName || (data.entryAmount && data.entryAmount > 0)) {
    receipt += `Entrada: ${normalizeForPrinter(data.entryTypeName || "")}\n`;
    if (data.entryMode === "fixed") {
      receipt += `Valor: +${formatCurrency(data.entryAmount || 0)}\n`;
    } else if (data.entryMode === "minimum_consumption") {
      receipt += `Consumacao min: ${formatCurrency(data.entryAmount || 0)}\n`;
      if (data.entryDifference && data.entryDifference > 0) {
        receipt += `Dif. consumacao: +${formatCurrency(data.entryDifference)}\n`;
      }
    }
  }

  receipt += sepLight;
  receipt += getTotalsStart();
  receipt += `TOTAL: ${formatCurrency(data.total)}\n`;
  receipt += getTotalsEnd();
  receipt += sepLight;
  receipt += `Pagamento: ${normalizeForPrinter(data.paymentMethod)}\n`;
  
  if (data.amountReceived !== undefined) {
    receipt += `Recebido: ${formatCurrency(data.amountReceived)}\n`;
  }
  
  if (data.change !== undefined && data.change > 0) {
    receipt += `Troco: ${formatCurrency(data.change)}\n`;
  }
  
  receipt += sep;
  
  // Footer from template
  receipt += ESCPOS.ALIGN_CENTER;
  const footerText = template?.footer_text ?? "Obrigado pela preferencia!";
  if (footerText) {
    receipt += normalizeForPrinter(footerText) + "\n";
  }
  if (template?.show_software_footer !== false) {
    receipt += "Altus Food\n";
  }
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += "\n\n";

  return receipt;
}

// Cash Movement Receipt (Sangria/Suprimento)
interface CashMovementReceiptData {
  type: "withdrawal" | "supply";
  amount: number;
  reason?: string;
  operatorName?: string;
  restaurantName?: string;
}

export function formatCashMovementReceipt(data: CashMovementReceiptData, charsPerLine: number = 48, template?: {
  title_bold: boolean;
  title_size: "normal" | "double";
  header_text: string | null;
  items_bold: boolean;
  items_size: "normal" | "double";
  totals_bold: boolean;
  totals_size: "normal" | "double";
  footer_text: string | null;
  show_software_footer: boolean;
  separator_char: string;
}): string {
  const date = new Date();
  const dateStr = date.toLocaleDateString("pt-BR");
  const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  const formatCurrency = (value: number) =>
    normalizeForPrinter(new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value));

  const isWithdrawal = data.type === "withdrawal";
  const title = isWithdrawal ? "SANGRIA DE CAIXA" : "SUPRIMENTO DE CAIXA";

  // Use template settings or defaults
  const separatorChar = template?.separator_char || "=";
  const sep = separatorChar.repeat(charsPerLine) + "\n";
  const sepLight = "-".repeat(charsPerLine) + "\n";

  // Helper functions for formatting based on template
  const getTitleStart = () => {
    let cmd = "";
    if (template?.title_bold) cmd += ESCPOS.BOLD_ON;
    if (template?.title_size === "double") cmd += ESCPOS.DOUBLE_SIZE_ON;
    return cmd;
  };
  const getTitleEnd = () => {
    let cmd = "";
    if (template?.title_size === "double") cmd += ESCPOS.DOUBLE_SIZE_OFF;
    if (template?.title_bold) cmd += ESCPOS.BOLD_OFF;
    return cmd;
  };
  const getTotalsStart = () => {
    let cmd = "";
    if (template?.totals_bold !== false) cmd += ESCPOS.BOLD_ON;
    if (template?.totals_size === "double") cmd += ESCPOS.DOUBLE_SIZE_ON;
    return cmd;
  };
  const getTotalsEnd = () => {
    let cmd = "";
    if (template?.totals_size === "double") cmd += ESCPOS.DOUBLE_SIZE_OFF;
    if (template?.totals_bold !== false) cmd += ESCPOS.BOLD_OFF;
    return cmd;
  };

  let receipt = "";
  receipt += sep;
  receipt += getTitleStart();
  receipt += ESCPOS.ALIGN_CENTER;
  receipt += title + "\n";
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += getTitleEnd();
  receipt += sep;
  
  // Header text from template
  if (template?.header_text) {
    receipt += ESCPOS.ALIGN_CENTER;
    receipt += normalizeForPrinter(template.header_text) + "\n";
    receipt += ESCPOS.ALIGN_LEFT;
  }
  
  if (data.restaurantName) {
    receipt += `${normalizeForPrinter(data.restaurantName)}\n`;
  }
  
  receipt += `Data: ${dateStr} ${timeStr}\n`;
  receipt += sepLight;
  receipt += getTotalsStart();
  receipt += `${formatCurrency(data.amount)}\n`;
  receipt += getTotalsEnd();
  
  if (data.reason) {
    receipt += `Motivo: ${normalizeForPrinter(data.reason)}\n`;
  }
  
  if (data.operatorName) {
    receipt += `Operador: ${normalizeForPrinter(data.operatorName)}\n`;
  }
  
  receipt += sepLight;
  receipt += "Assinatura: ___________________\n";
  receipt += sep;
  
  // Footer from template
  receipt += ESCPOS.ALIGN_CENTER;
  if (template?.footer_text) {
    receipt += normalizeForPrinter(template.footer_text) + "\n";
  }
  if (template?.show_software_footer !== false) {
    receipt += "Altus Food\n";
  }
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += "\n\n";

  return receipt;
}

// Cash Register Close Receipt
interface CashRegisterCloseReceiptData {
  restaurantName?: string;
  openedAt: string;
  closedAt: string;
  operatorName?: string;
  openingAmount: number;
  supplies: number;
  withdrawals: number;
  cashSales: number;
  expectedAmount: number;
  countedAmount: number;
  difference: number;
  notes?: string;
  cashCountDetails?: Array<{
    denomination: number;
    quantity: number;
    total: number;
    type: "note" | "coin";
  }>;
  // New fields for enhanced receipt
  totalRevenue?: number;
  subtotalProducts?: number;
  totalDiscount?: number;
  totalServiceTax?: number;
  totalCouvert?: number;
  salesByPaymentMethod?: Array<{
    name: string;
    total: number;
  }>;
  cancellations?: Array<{
    productName: string;
    quantity: number;
    totalValue: number;
    cancelledBy: string;
    reason?: string;
  }>;
  totalCancellations?: number;
  salesByOrderType?: Array<{
    orderType: string;
    label: string;
    total: number;
    count: number;
    sources?: Array<{ source: string; label: string; total: number; count: number }>;
  }>;
}

export function formatCashRegisterCloseReceipt(data: CashRegisterCloseReceiptData, charsPerLine: number = 48): string {
  const formatCurrency = (value: number) =>
    normalizeForPrinter(new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value));

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR") + " " + date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  const sep = "=".repeat(charsPerLine) + "\n";
  const sepLight = "-".repeat(charsPerLine) + "\n";
  const title = "FECHAMENTO DE CAIXA";
  const centerPad = Math.max(0, Math.floor((charsPerLine - title.length) / 2));
  const labelWidth = 22;
  const valueWidth = Math.max(10, charsPerLine - labelWidth);

  const formatLine = (label: string, value: string) => {
    const paddedLabel = label.padEnd(labelWidth);
    return paddedLabel + value.padStart(valueWidth) + "\n";
  };

  let receipt = "";
  receipt += sep;
  receipt += " ".repeat(centerPad) + title + "\n";
  receipt += sep;
  
  if (data.restaurantName) {
    receipt += `${normalizeForPrinter(data.restaurantName)}\n`;
  }
  
  receipt += sepLight;
  receipt += `Abertura: ${formatDate(data.openedAt)}\n`;
  receipt += `Fechamento: ${formatDate(data.closedAt)}\n`;
  
  if (data.operatorName) {
    receipt += `Operador: ${normalizeForPrinter(data.operatorName)}\n`;
  }
  
  // ============ FATURAMENTO DO TURNO ============
  if (data.totalRevenue !== undefined) {
    receipt += sep;
    const fatTitle = "FATURAMENTO DO TURNO";
    const fatPad = Math.max(0, Math.floor((charsPerLine - fatTitle.length) / 2));
    receipt += " ".repeat(fatPad) + fatTitle + "\n";
    receipt += sepLight;
    
    if (data.subtotalProducts !== undefined) {
      receipt += formatLine("Subtotal Produtos:", formatCurrency(data.subtotalProducts));
    }
    if (data.totalServiceTax !== undefined && data.totalServiceTax > 0) {
      receipt += formatLine("(+) Taxa Servico 10%:", formatCurrency(data.totalServiceTax));
    }
    if (data.totalCouvert !== undefined && data.totalCouvert > 0) {
      receipt += formatLine("(+) Couvert:", formatCurrency(data.totalCouvert));
    }
    if (data.totalDiscount !== undefined && data.totalDiscount > 0) {
      receipt += formatLine("(-) Descontos:", formatCurrency(data.totalDiscount));
    }
    receipt += sepLight;
    receipt += ESCPOS.BOLD_ON;
    receipt += formatLine("FATURAMENTO TOTAL:", formatCurrency(data.totalRevenue));
    receipt += ESCPOS.BOLD_OFF;
  }
  
  // ============ VENDAS POR FORMA DE PAGAMENTO ============
  if (data.salesByPaymentMethod && data.salesByPaymentMethod.length > 0) {
    receipt += sep;
    const pmTitle = "VENDAS POR PAGAMENTO";
    const pmPad = Math.max(0, Math.floor((charsPerLine - pmTitle.length) / 2));
    receipt += " ".repeat(pmPad) + pmTitle + "\n";
    receipt += sepLight;
    
    for (const pm of data.salesByPaymentMethod) {
      receipt += formatLine(`${normalizeForPrinter(pm.name)}:`, formatCurrency(pm.total));
    }
  }

  // ============ VENDAS POR TIPO DE PEDIDO ============
  if (data.salesByOrderType && data.salesByOrderType.length > 0) {
    receipt += sep;
    const otTitle = "VENDAS POR TIPO";
    const otPad = Math.max(0, Math.floor((charsPerLine - otTitle.length) / 2));
    receipt += " ".repeat(otPad) + otTitle + "\n";
    receipt += sepLight;

    let totalAll = 0;
    let countAll = 0;
    for (const t of data.salesByOrderType) {
      const label = `${normalizeForPrinter(t.label)} (${t.count}):`;
      receipt += formatLine(label, formatCurrency(t.total));
      totalAll += t.total;
      countAll += t.count;
      if (t.sources && t.sources.length > 0) {
        for (const s of t.sources) {
          receipt += formatLine(
            `  - ${normalizeForPrinter(s.label)} (${s.count}):`,
            formatCurrency(s.total)
          );
        }
      }
    }
    receipt += sepLight;
    receipt += ESCPOS.BOLD_ON;
    receipt += formatLine(`TOTAL (${countAll}):`, formatCurrency(totalAll));
    receipt += ESCPOS.BOLD_OFF;
  }

  // ============ CONTROLE DE CAIXA ============
  receipt += sep;
  const caixaTitle = "CONTROLE DE CAIXA";
  const caixaPad = Math.max(0, Math.floor((charsPerLine - caixaTitle.length) / 2));
  receipt += " ".repeat(caixaPad) + caixaTitle + "\n";
  receipt += sepLight;
  
  receipt += formatLine("Fundo Inicial:", formatCurrency(data.openingAmount));
  receipt += formatLine("(+) Suprimentos:", formatCurrency(data.supplies));
  receipt += formatLine("(-) Sangrias:", formatCurrency(data.withdrawals));
  receipt += formatLine("(+) Vendas Dinheiro:", formatCurrency(data.cashSales));
  receipt += sepLight;
  receipt += ESCPOS.BOLD_ON;
  receipt += formatLine("VALOR ESPERADO:", formatCurrency(data.expectedAmount));
  receipt += ESCPOS.BOLD_OFF;
  receipt += sepLight;
  receipt += formatLine("VALOR INFORMADO:", formatCurrency(data.countedAmount));
  receipt += sepLight;
  
  const diffLabel = data.difference >= 0 ? "DIFERENCA (SOBRA):" : "DIFERENCA (FALTA):";
  const diffValue = Math.abs(data.difference);
  if (diffValue > 0) {
    receipt += ESCPOS.BOLD_ON;
  }
  receipt += formatLine(diffLabel, formatCurrency(diffValue));
  if (diffValue > 0) {
    receipt += ESCPOS.BOLD_OFF;
  }
  
  // ============ CANCELAMENTOS DO TURNO ============
  if (data.cancellations && data.cancellations.length > 0) {
    receipt += sep;
    const cancTitle = "CANCELAMENTOS DO TURNO";
    const cancPad = Math.max(0, Math.floor((charsPerLine - cancTitle.length) / 2));
    receipt += " ".repeat(cancPad) + cancTitle + "\n";
    receipt += sepLight;
    
    for (const canc of data.cancellations) {
      receipt += `${normalizeForPrinter(canc.productName)} (x${canc.quantity})\n`;
      receipt += `  Valor: ${formatCurrency(canc.totalValue)}\n`;
      if (canc.reason) {
        receipt += `  Motivo: ${normalizeForPrinter(canc.reason)}\n`;
      }
      receipt += `  Por: ${normalizeForPrinter(canc.cancelledBy)}\n`;
      receipt += sepLight;
    }
    
    if (data.totalCancellations !== undefined && data.totalCancellations > 0) {
      receipt += ESCPOS.BOLD_ON;
      receipt += formatLine("TOTAL CANCELAMENTOS:", formatCurrency(data.totalCancellations));
      receipt += ESCPOS.BOLD_OFF;
    }
  }
  
  // ============ CONTAGEM DETALHADA ============
  if (data.cashCountDetails && data.cashCountDetails.length > 0) {
    receipt += sep;
    const countTitle = "CONTAGEM DETALHADA";
    const countPad = Math.max(0, Math.floor((charsPerLine - countTitle.length) / 2));
    receipt += " ".repeat(countPad) + countTitle + "\n";
    receipt += sepLight;
    
    const notes = data.cashCountDetails.filter(d => d.type === "note" && d.quantity > 0);
    const coins = data.cashCountDetails.filter(d => d.type === "coin" && d.quantity > 0);
    
    if (notes.length > 0) {
      receipt += "CEDULAS:\n";
      for (const item of notes) {
        const denom = formatCurrency(item.denomination);
        const total = formatCurrency(item.total);
        receipt += `${denom} x ${item.quantity} = ${total}\n`;
      }
    }
    
    if (coins.length > 0) {
      receipt += "MOEDAS:\n";
      for (const item of coins) {
        const denom = formatCurrency(item.denomination);
        const total = formatCurrency(item.total);
        receipt += `${denom} x ${item.quantity} = ${total}\n`;
      }
    }
  }
  
  if (data.notes) {
    receipt += sepLight;
    receipt += `Obs: ${normalizeForPrinter(data.notes)}\n`;
  }
  
  receipt += sep;
  receipt += "Assinatura: ___________________\n";
  receipt += sep;
  
  // Software footer
  receipt += ESCPOS.ALIGN_CENTER;
  receipt += "Altus Food\n";
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += "\n\n";

  return receipt;
}

// Conference Bill (Conferência de Conta)
export interface ConferenceBillData {
  orderNumber: number;
  items: {
    name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    notes?: string;
  }[];
  subtotal: number;
  serviceTax?: number;
  serviceTaxPercentage?: number;
  couvertAmount?: number;
  couvertQuantity?: number;
  total: number;
  customerName?: string;
  tableNumber?: string;
  tabNumber?: string;
  restaurantName?: string;
  restaurantAddress?: string;
  // Entry information
  entryTypeName?: string;
  entryAmount?: number;
  entryMode?: "fixed" | "minimum_consumption";
  entryDifference?: number;
  peopleCount?: number;
}

export function formatConferenceBill(data: ConferenceBillData, charsPerLine: number = 48, template?: {
  title_bold: boolean;
  title_size: "normal" | "double";
  header_text: string | null;
  items_bold: boolean;
  items_size: "normal" | "double";
  totals_bold: boolean;
  totals_size: "normal" | "double";
  footer_text: string | null;
  show_software_footer: boolean;
  separator_char: string;
}): string {
  const date = new Date();
  const dateStr = date.toLocaleDateString("pt-BR");
  const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  const formatCurrency = (value: number) =>
    normalizeForPrinter(new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value));

  // Use template settings or defaults
  const separatorChar = template?.separator_char || "=";
  const sep = separatorChar.repeat(charsPerLine) + "\n";
  const sepLight = "-".repeat(charsPerLine) + "\n";
  const title = "CONFERENCIA DE CONTA";
  const centerPad = Math.max(0, Math.floor((charsPerLine - title.length) / 2));
  const valueWidth = Math.max(10, charsPerLine - 20);

  // Group identical items (same name and unit price)
  const groupedItems = data.items.reduce((acc, item) => {
    const key = `${item.name}-${item.unit_price}`;
    if (acc.has(key)) {
      const existing = acc.get(key)!;
      existing.quantity += item.quantity;
      existing.total_price += item.total_price;
      // Concatenate notes if different
      if (item.notes && existing.notes !== item.notes) {
        existing.notes = existing.notes ? `${existing.notes}; ${item.notes}` : item.notes;
      }
    } else {
      acc.set(key, { ...item });
    }
    return acc;
  }, new Map<string, typeof data.items[0]>());
  const consolidatedItems = Array.from(groupedItems.values());

  // Helper functions for formatting based on template
  const getTitleStart = () => {
    let cmd = "";
    if (template?.title_bold) cmd += ESCPOS.BOLD_ON;
    if (template?.title_size === "double") cmd += ESCPOS.DOUBLE_SIZE_ON;
    return cmd;
  };
  const getTitleEnd = () => {
    let cmd = "";
    if (template?.title_size === "double") cmd += ESCPOS.DOUBLE_SIZE_OFF;
    if (template?.title_bold) cmd += ESCPOS.BOLD_OFF;
    return cmd;
  };
  const getItemsStart = () => {
    let cmd = "";
    if (template?.items_bold) cmd += ESCPOS.BOLD_ON;
    if (template?.items_size === "double") cmd += ESCPOS.DOUBLE_SIZE_ON;
    return cmd;
  };
  const getItemsEnd = () => {
    let cmd = "";
    if (template?.items_size === "double") cmd += ESCPOS.DOUBLE_SIZE_OFF;
    if (template?.items_bold) cmd += ESCPOS.BOLD_OFF;
    return cmd;
  };
  const getTotalsStart = () => {
    let cmd = "";
    if (template?.totals_bold) cmd += ESCPOS.BOLD_ON;
    if (template?.totals_size === "double") cmd += ESCPOS.DOUBLE_SIZE_ON;
    return cmd;
  };
  const getTotalsEnd = () => {
    let cmd = "";
    if (template?.totals_size === "double") cmd += ESCPOS.DOUBLE_SIZE_OFF;
    if (template?.totals_bold) cmd += ESCPOS.BOLD_OFF;
    return cmd;
  };

  let receipt = "";
  receipt += sep;
  receipt += getTitleStart();
  receipt += ESCPOS.ALIGN_CENTER;
  receipt += title + "\n";
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += getTitleEnd();
  receipt += sep;
  
  // Header text from template
  if (template?.header_text) {
    receipt += ESCPOS.ALIGN_CENTER;
    receipt += normalizeForPrinter(template.header_text) + "\n";
    receipt += ESCPOS.ALIGN_LEFT;
  }
  
  if (data.restaurantName) {
    receipt += `${normalizeForPrinter(data.restaurantName)}\n`;
  }
  if (data.restaurantAddress) {
    receipt += `${normalizeForPrinter(data.restaurantAddress)}\n`;
  }
  
  receipt += `Data: ${dateStr} ${timeStr}\n`;
  receipt += sepLight;
  
  if (data.tableNumber) {
    receipt += `Mesa: ${data.tableNumber}`;
  } else if (data.tabNumber) {
    receipt += `Comanda: ${data.tabNumber}`;
  }
  receipt += `        Pedido: #${data.orderNumber}\n`;
  
  if (data.customerName) {
    receipt += `Cliente: ${normalizeForPrinter(data.customerName)}\n`;
  }
  
  receipt += sepLight;
  receipt += "ITENS:\n";
  receipt += sepLight;

  receipt += getItemsStart();
  for (const item of consolidatedItems) {
    // Formato: "2x Produto            R$ 24,00"
    const itemText = `${item.quantity}x ${normalizeForPrinter(item.name)}`;
    const priceText = formatCurrency(item.total_price);
    const padding = Math.max(1, charsPerLine - itemText.length - priceText.length);
    receipt += itemText + " ".repeat(padding) + priceText + "\n";
    if (item.notes) {
      receipt += `   (${normalizeForPrinter(item.notes)})\n`;
    }
  }
  receipt += getItemsEnd();

  receipt += sepLight;
  receipt += getTotalsStart();
  receipt += `Subtotal:${formatCurrency(data.subtotal).padStart(valueWidth)}\n`;
  
  if (data.couvertQuantity && data.couvertQuantity > 0 && data.couvertAmount) {
    receipt += `Couvert (${data.couvertQuantity}x):+${formatCurrency(data.couvertAmount).padStart(valueWidth)}\n`;
  }
  
  if (data.serviceTax && data.serviceTax > 0) {
    const taxLabel = data.serviceTaxPercentage ? `Taxa Serv. ${data.serviceTaxPercentage}%` : "Taxa Servico";
    receipt += `${taxLabel}:+${formatCurrency(data.serviceTax).padStart(valueWidth)}\n`;
  }
  
  // Entry information
  if (data.entryTypeName) {
    receipt += sepLight;
    receipt += `Entrada: ${normalizeForPrinter(data.entryTypeName)}\n`;
    if (data.entryMode === "fixed") {
      receipt += `Valor:${formatCurrency(data.entryAmount || 0).padStart(valueWidth)}\n`;
    } else if (data.entryMode === "minimum_consumption") {
      receipt += `Consumacao min:${formatCurrency(data.entryAmount || 0).padStart(valueWidth)}\n`;
      if (data.entryDifference && data.entryDifference > 0) {
        receipt += `Dif. consumacao:+${formatCurrency(data.entryDifference).padStart(valueWidth)}\n`;
      }
    }
  }
  
  receipt += sepLight;
  receipt += `TOTAL:${formatCurrency(data.total).padStart(valueWidth)}\n`;
  if (data.peopleCount && data.peopleCount > 1) {
    const perPerson = formatCurrency(data.total / data.peopleCount);
    receipt += `Valor p/ pessoa (${data.peopleCount}x):${perPerson.padStart(Math.max(1, valueWidth - String(data.peopleCount).length - 18))}\n`;
  }
  receipt += getTotalsEnd();
  receipt += sep;
  const note1 = "* CONFERENCIA - NAO E CUPOM *";
  const note2 = "* Aguarde para finalizar *";
  const note1Pad = Math.max(0, Math.floor((charsPerLine - note1.length) / 2));
  const note2Pad = Math.max(0, Math.floor((charsPerLine - note2.length) / 2));
  receipt += " ".repeat(note1Pad) + note1 + "\n";
  receipt += " ".repeat(note2Pad) + note2 + "\n";
  receipt += sep;
  
  // Footer from template
  receipt += ESCPOS.ALIGN_CENTER;
  if (template?.footer_text) {
    receipt += normalizeForPrinter(template.footer_text) + "\n";
  }
  if (template?.show_software_footer !== false) {
    receipt += "Altus Food\n";
  }
  receipt += ESCPOS.ALIGN_LEFT;
  receipt += "\n\n";

  return receipt;
}
